import { Config, newNameStrIdConfig } from 'app/common/common/search-select/search-select.config';

type TemplateBlockMetadata = {
  labels?: string[];
  newText?: string; // FE only
  textParts?: string[]; // FE only
  textLabels?: string[]; // FE only
  preview?: string;
  configs?: {
    type: 'dropdown';
    vals: {
      id: string;
      name: string;
      includeFor: string[]; // TODO: To be implemented.
    }[];
    forLabel: string;
    replaceText: string;
    configObj?: Config; // FE only
    selectedOpt?: string; // FE only
  }[];
};

export class BTemplateBlock {
  id: number;
  name: string;
  text: string;
  metadata: TemplateBlockMetadata;
  type: number;
  typeName: 'administration' | 'all';

  editable: boolean;
  hasDropdownConfig: boolean;
  hasImgUploadConfig: boolean;

  static fromRest(json: any): BTemplateBlock {
    if (json) {
      json.editable = !!json.metadata?.labels?.length;
      (json.metadata.configs || []).forEach((config) => {
        if (config.type == 'dropdown') {
          config.configObj = newNameStrIdConfig(config.vals, config.forLabel);
          config.selectedOpt = undefined;
        }
      });
      json.hasDropdownConfig = json.metadata.configs?.some((c) => c.type == 'dropdown');
      json.hasImgUploadConfig = json.metadata.configs?.some((c) => c.type == 'imgUpload');
      return Object.assign(new BTemplateBlock(), json);
    }
    return json;
  }

  static fromRestArray(json: any[]): BTemplateBlock[] {
    return json && json.map((v) => BTemplateBlock.fromRest(v));
  }

  getPreview(): string {
    let preview = this.metadata.preview;
    if (this.hasImgUploadConfig) {
      preview = 'Image';
    } else if (this.hasDropdownConfig) {
      const text = this.getTextParts()[0];
      preview = `Date ${text} time (e.g. 9th May 2025 ${text} 14:30)`;
    } else if (this.editable) {
      preview = this.getTextParts().join('/');
    }
    return preview;
  }

  getTextParts(): string[] {
    const text = this.metadata?.newText || this.text;
    if (
      [
        'Spam messages',
        'Merge preference texts for merge as error message',
        'First follow-up reminder created date and time with pre and post text',
        'Department name and email with pre texts.',
        'Processed roles list',
      ].includes(this.name)
    ) {
      return text.split(/{%.*?%}|{{.*?}}|<[^>]*>/g).filter((s) => s.trim().length > 0);
    }

    if (text.startsWith('<') && text.endsWith('>')) {
      let editableText = text.replace(/<[^>]+>/g, '');
      if (this.hasDropdownConfig) {
        editableText = text.replace(/<[^>]+>|{{[^}]+}}/g, '');
      } else if (this.hasImgUploadConfig) {
        const styles = text
          .match(/style="([^"]*)"/)?.[1]
          .split(';')
          .filter(Boolean)
          .map((s) => s.split(':'));
        return [...styles, text.match(/src="([^"]*)"/)].map((t) => t?.[1] || '');
      }
      return editableText ? [editableText] : [];
    }

    return [...text.matchAll(/%\}(.*?)\{%/g)]
      .map((m) => m[1].replace(/\s*\{\{.*?\}\}|\s*<[^>]*>/g, ''))
      .filter((t) => t.trim());
  }

  prepareForEdit(): void {
    this.prepareTextForEdit();
    this.metadata.newText = this.text;
    this.metadata.textParts = this.getTextParts();
    this.metadata.textLabels = this.metadata.labels.filter(
      (l) => !this.metadata.configs?.find((c) => c.forLabel == l)
    );
    if (this.hasDropdownConfig) {
      this.metadata.newText = this.metadata.newText.replace(
        /(<b>{{[^}]+}}<\/b>)([^<]+)(<b>{{[^}]+}}<\/b>)/,
        '$1PLACEHOLDER_0$3'
      );
    } else {
      this.metadata.textParts.forEach((part, idx) => {
        this.metadata.newText = this.metadata.newText.replace(part, `PLACEHOLDER_${idx}`);
      });
    }
  }

  static serialize(blocks: BTemplateBlock[]): string {
    let str = '';
    blocks.forEach((block) => {
      let text = block.text;
      if (block.metadata?.textParts?.length) {
        block.metadata.textParts.forEach(
          (part, idx) =>
            (block.metadata.newText = block.metadata.newText.replace(`PLACEHOLDER_${idx}`, part))
        );
        text = block.metadata.newText;
      }

      (block.metadata.configs || []).forEach((config) => {
        if (config.selectedOpt) {
          text = text.replace(new RegExp(config.replaceText, 'g'), config.selectedOpt);
        }
      });

      str += ` <span data-block-id='${block.id}'>${text}</span> `;
    });
    return str.trim();
  }

  static deserialize(htmlStr: string, allBlocks: BTemplateBlock[]): BTemplateBlock[] {
    const blocks = [];

    const container = document.createElement('div');
    container.innerHTML = htmlStr;
    container.querySelectorAll('[data-block-id]').forEach((span) => {
      const id = Number(span.getAttribute('data-block-id'));
      const block = allBlocks.find((b) => b.id == id);
      blocks.push(
        BTemplateBlock.fromRest({
          id,
          name: block.name,
          text: span.innerHTML,
          metadata: block.resolveMetadata(),
        })
      );
    });
    return blocks;
  }

  private resolveMetadata(): TemplateBlockMetadata {
    const metadata: TemplateBlockMetadata = {};
    if (this.metadata?.labels?.length) {
      metadata.labels = this.metadata.labels;
    }
    if (this.metadata?.preview) {
      metadata.preview = this.metadata.preview;
    }
    if (this.metadata.configs) {
      metadata.configs = this.metadata.configs;
    }
    return metadata;
  }

  private prepareTextForEdit(): void {
    if (this.hasDropdownConfig) {
      this.metadata.configs.forEach((config) => {
        config.vals.forEach((val) => {
          if (this.text.includes(val.id)) {
            config.selectedOpt = val.id;
            this.text = this.text.replace(new RegExp(val.id, 'g'), config.replaceText);
          }
        });
      });
    }
  }
}
